import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import InlineScanner from '@/components/InlineScanner';
import { unlockAudio } from '@/lib/scanFeedback';
import BarcodeAutocomplete from '@/components/BarcodeAutocomplete';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { uid, fmtDate, money } from '@/lib/helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanLine, X, Loader2, ArrowRight, Truck, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function Transfers() {
  const { stores, currentStore } = useApp();
  const qc = useQueryClient();
  const [tab, setTab] = useState('create');
  const [fromStore, setFromStore] = useState(currentStore);
  const [toStore, setToStore] = useState(stores.find((s) => s.id !== currentStore)?.id || '');
  const [items, setItems] = useState([]);
  const [manual, setManual] = useState('');
  const [scanActive, setScanActive] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type:'ok'|'err', code, msg }
  const [saving, setSaving] = useState(false);
  const addingRef = React.useRef(new Set());
  const fbTimer = React.useRef(null);

  const { data: transfers = [] } = useQuery({ queryKey: ['transfers'], queryFn: async () => (await api.get('/transfers')).data.data });

  const showFeedback = (type, code, msg, fromScan) => {
    setFeedback({ type, code, msg });
    if (fbTimer.current) clearTimeout(fbTimer.current);
    // Errors linger a little longer so they can be read while scanning.
    fbTimer.current = setTimeout(() => setFeedback(null), type === 'ok' ? 2200 : 4000);
    // Toast only for manual entry (inline banner covers the scanning flow).
    if (!fromScan) { type === 'ok' ? toast.success(`${code} added`) : toast.error(`${code}: ${msg}`); }
    if (type === 'err' && navigator.vibrate) { try { navigator.vibrate([60, 40, 60]); } catch (e) { /* ignore */ } }
  };

  const addCode = async (raw, fromScan = false) => {
    const bc = String(raw).trim().toUpperCase();
    if (!bc) return;
    // Guard the async window: ignore if already added or currently being added.
    if (addingRef.current.has(bc) || items.some((i) => i.barcode === bc)) {
      showFeedback('err', bc, 'Already added to this transfer.', fromScan);
      return;
    }
    addingRef.current.add(bc);
    try {
      const g = (await api.get(`/garments/lookup/${encodeURIComponent(bc)}`)).data.data;
      if (g.status === 'SOLD') { showFeedback('err', bc, 'Item already sold — cannot be transferred.', fromScan); return; }
      if (g.status === 'IN_TRANSIT') { showFeedback('err', bc, 'Already part of another transfer.', fromScan); return; }
      if (g.store_id === toStore) { showFeedback('err', bc, `Already available in ${g.store_name || 'destination store'}.`, fromScan); return; }
      if (g.store_id !== fromStore) { showFeedback('err', bc, `Not available at source — it is in ${g.store_name}.`, fromScan); return; }
      // Final functional dedup guard against React state races.
      let added = false;
      setItems((c) => { if (c.some((i) => i.barcode === bc)) return c; added = true; return [g, ...c]; });
      showFeedback('ok', bc, g.product || 'Added', fromScan);
    } catch (e) {
      showFeedback('err', bc, e?.response?.status === 404 ? 'Barcode not found.' : errMsg(e), fromScan);
    } finally {
      addingRef.current.delete(bc);
    }
  };

  const submit = async () => {
    if (!items.length) { toast.error('Add garments to transfer.'); return; }
    if (fromStore === toStore) { toast.error('Source and destination must differ.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/transfers', { from_store: fromStore, to_store: toStore, barcodes: items.map((i) => i.barcode), immediate: true, client_txn_id: uid() });
      toast.success(`Transfer ${r.data.data.transfer_no} · ${r.data.data.count} garments moved`);
      setItems([]); qc.invalidateQueries({ queryKey: ['transfers'] }); setTab('history');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const receive = async (id) => {
    try { await api.post(`/transfers/${id}/receive`, { client_txn_id: uid() }); toast.success('Transfer received'); qc.invalidateQueries({ queryKey: ['transfers'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Page>
      <TopBar title="Transfers" />
      <div className="px-4 pt-4">
        <Tabs value={tab} onValueChange={(v) => { setScanActive(false); setTab(v); }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="create" data-testid="transfers-tab-create">New Transfer</TabsTrigger>
            <TabsTrigger value="history" data-testid="transfers-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-4 space-y-3">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-end gap-2">
                <div className="flex-1"><Label className="text-xs">From</Label>
                  <Select value={fromStore} onValueChange={(v) => { setFromStore(v); setItems([]); }}>
                    <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="transfer-from"><SelectValue /></SelectTrigger>
                    <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <ArrowRight className="mb-3 h-5 w-5 text-muted-foreground" />
                <div className="flex-1"><Label className="text-xs">To</Label>
                  <Select value={toStore} onValueChange={setToStore}>
                    <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="transfer-to"><SelectValue placeholder="Store" /></SelectTrigger>
                    <SelectContent>{stores.filter((s) => s.id !== fromStore).map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <div className="flex-1">
                  <BarcodeAutocomplete storeId={fromStore} statusFilter="AVAILABLE" testId="transfer-manual"
                    placeholder="Search / add garment code" onSelect={(g) => addCode(g.barcode)} onSubmit={(code) => addCode(code)} />
                </div>
                <Button variant={scanActive ? 'default' : 'secondary'} className="h-12 w-12 shrink-0 rounded-xl p-0"
                  onClick={() => { unlockAudio(); setScanActive((v) => !v); }} data-testid="transfer-scan"
                  aria-pressed={scanActive} aria-label={scanActive ? 'Close scanner' : 'Open scanner'}>
                  {scanActive ? <X className="h-5 w-5" /> : <ScanLine className="h-5 w-5" />}
                </Button>
              </div>
              {scanActive && (
                <InlineScanner onDetected={(code) => addCode(code, true)} onClose={() => setScanActive(false)} />
              )}
              {feedback && (
                <div
                  data-testid="scan-feedback"
                  className={`mt-2 flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${feedback.type === 'ok'
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'bg-destructive/10 text-destructive'}`}
                >
                  {feedback.type === 'ok'
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                  <div className="min-w-0">
                    <span className="font-mono font-semibold">{feedback.code}</span>
                    <span className="ml-1.5 text-xs">{feedback.type === 'ok' ? `added · ${feedback.msg}` : feedback.msg}</span>
                  </div>
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{items.length} garment{items.length === 1 ? '' : 's'} to transfer</p>
                <div className="max-h-64 space-y-2 overflow-y-auto overscroll-contain pr-0.5" data-testid="transfer-items-list">
                  {items.map((g) => (
                    <div key={g.barcode} className="flex items-center justify-between gap-2 rounded-xl bg-secondary/40 p-2.5" data-testid={`transfer-item-${g.barcode}`}>
                      <div className="min-w-0"><p className="truncate text-sm font-medium">{g.product}</p><p className="font-mono text-xs text-muted-foreground">{g.barcode} · {g.size || '-'} · {g.colour || '-'} · {money(g.price)}</p></div>
                      <button onClick={() => setItems((x) => x.filter((y) => y.barcode !== g.barcode))} data-testid={`transfer-item-remove-${g.barcode}`} aria-label={`Remove ${g.barcode}`}><X className="h-4 w-4 text-destructive" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="transfer-confirm" className="h-12 w-full rounded-xl font-semibold" onClick={submit} disabled={saving || !items.length}>
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : `Confirm Transfer (${items.length})`}
            </Button>
          </TabsContent>

          <TabsContent value="history" className="mt-4 space-y-2">
            {transfers.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No transfers yet.</p> :
              transfers.map((t) => (
                <div key={t.id} data-testid={`transfer-${t.transfer_no}`} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm font-semibold">{t.transfer_no}</p>
                      <p className="text-xs text-muted-foreground">{t.from_store_name} → {t.to_store_name} · {t.count} items</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</p>
                    </div>
                    {t.status === 'SENT' ? (
                      <Button size="sm" className="h-9 rounded-lg" onClick={() => receive(t.id)} data-testid={`transfer-receive-${t.transfer_no}`}><Truck className="mr-1 h-4 w-4" />Receive</Button>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" />{t.status}</span>
                    )}
                  </div>
                </div>
              ))}
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  );
}
